import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useMedicosAnalyticsOdf } from '../hooks/useAnalyticsOdf'
import type { FiltrosReporteOdf } from '../types'

const nf = (n: number) => n.toLocaleString('es-EC')

interface MedicosTabProps {
  filtros: FiltrosReporteOdf
}

interface DimensionTableProps {
  titulo: string
  filas: { clave: string; total: number }[]
}

function DimensionTable({ titulo, filas }: DimensionTableProps) {
  const sorted = [...filas].sort((a, b) => b.total - a.total)
  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="text-sm font-medium">
          {titulo} ({nf(sorted.length)})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Médicos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length > 0 ? (
                sorted.map((row) => (
                  <TableRow key={row.clave}>
                    <TableCell className="font-medium">{row.clave || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{nf(row.total)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground py-10">
                    Sin datos disponibles.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

export function MedicosTab({ filtros }: MedicosTabProps) {
  const { data, isLoading, error } = useMedicosAnalyticsOdf(filtros)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de médicos. Verifique la configuración e intente de nuevo.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <DimensionTable titulo="Por provincia" filas={data.por_provincia} />
      <DimensionTable titulo="Por especialidad" filas={data.por_especialidad} />
      <DimensionTable titulo="Por ciudad" filas={data.por_ciudad} />
    </div>
  )
}
